import React, { useState, useRef, useEffect, useMemo, lazy, Suspense } from 'react';
import { Search, Clock, X, Package, Tag, Barcode, Camera } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { useSearchHistory } from '@/hooks/useSearchHistory';
import { useSearchSuggestions } from '@/hooks/useSearchSuggestions';
import { cn } from '@/lib/utils';

const BarcodeScannerDialog = lazy(() => import('@/components/BarcodeScannerDialog').then(m => ({ default: m.BarcodeScannerDialog })));

interface RecallItem {
  product_name: string;
  brand: string;
  gtin?: string | null;
  batch_lot?: string | null;
}

interface SearchWithSuggestionsProps {
  value: string;
  onChange: (value: string) => void;
  onSearch: () => void;
  recalls: RecallItem[];
  placeholder?: string;
}

// Highlight le terme de recherche dans le texte
const HighlightedText = ({ text, highlight }: { text: string; highlight: string }) => {
  if (!highlight.trim()) return <>{text}</>;
  
  const regex = new RegExp(`(${highlight.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
  const parts = text.split(regex);
  
  return (
    <>
      {parts.map((part, i) => 
        regex.test(part) ? (
          <mark key={i} className="bg-primary/20 text-foreground rounded px-0.5">{part}</mark>
        ) : (
          <span key={i}>{part}</span>
        )
      )}
    </>
  );
};

export const SearchWithSuggestions = ({
  value,
  onChange,
  onSearch,
  recalls
}: SearchWithSuggestionsProps) => {
  const [open, setOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [scannerOpen, setScannerOpen] = useState(false);
  const [hasCamera, setHasCamera] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const { history, addToHistory, removeFromHistory, clearHistory } = useSearchHistory();
  const suggestions = useSearchSuggestions(recalls, value);


  // Detect camera availability — simple check for API support
  useEffect(() => {
    setHasCamera(!!navigator.mediaDevices?.getUserMedia);
  }, []);

  const showHistory = history.length > 0 && !value;
  const showSuggestions = suggestions.length > 0 && value.length >= 1;

  // Liste combinée des items navigables
  const navigableItems = useMemo(() => {
    if (showHistory) {
      return history.slice(0, 5).map(term => ({ text: term, type: 'history' as const }));
    }
    if (showSuggestions) {
      return suggestions.map(s => ({ text: s.text, type: s.type }));
    }
    return [];
  }, [showHistory, showSuggestions, history, suggestions]);

  // Reset l'index quand le contenu change
  useEffect(() => {
    setSelectedIndex(-1);
  }, [value, open]);

  const handleSelect = (term: string) => {
    onChange(term);
    setOpen(false);
    setSelectedIndex(-1);
    addToHistory(term);
    setTimeout(() => onSearch(), 50);
  };

  const handleClear = () => {
    onChange('');
    setSelectedIndex(-1);
    inputRef.current?.focus();
  };

  const handleScan = (gtin: string) => {
    onChange(gtin);
    addToHistory(gtin);
    setScannerOpen(false);
    setTimeout(() => onSearch(), 50);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    const itemCount = navigableItems.length;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!open && itemCount > 0) {
        setOpen(true);
      }
      setSelectedIndex(prev => (prev < itemCount - 1 ? prev + 1 : prev));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(prev => (prev > 0 ? prev - 1 : -1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (selectedIndex >= 0 && navigableItems[selectedIndex]) {
        handleSelect(navigableItems[selectedIndex].text);
      } else if (value.trim()) {
        addToHistory(value);
        onSearch();
        setOpen(false);
      }
    } else if (e.key === 'Escape') {
      setOpen(false);
      setSelectedIndex(-1);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    onChange(newValue);
    // Ouvrir dès la première lettre
    if (!open && newValue.length >= 1) {
      setOpen(true);
    }
  };

  const showPopover = open && (showHistory || showSuggestions);

  return (
    <>
    <Popover open={showPopover} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <div className="relative w-full">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            ref={inputRef}
            placeholder="Nom, marque ou code-barres..."
            value={value}
            onChange={handleInputChange}
            onFocus={() => setOpen(true)}
            onKeyDown={handleKeyDown}
            inputMode="search"
            className={cn("pl-10", value ? "pr-10" : hasCamera ? "pr-10" : "")}
          />
          {value ? (
            <Button
              variant="ghost"
              size="icon"
              className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 text-muted-foreground hover:text-foreground"
              onClick={handleClear}
              type="button"
            >
              <X className="h-4 w-4" />
              <span className="sr-only">Effacer la recherche</span>
            </Button>
          ) : hasCamera ? (
            <Button
              variant="ghost"
              size="icon"
              className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 text-muted-foreground hover:text-primary"
              onClick={() => setScannerOpen(true)}
              type="button"
            >
              <Camera className="h-4 w-4" />
              <span className="sr-only">Scanner un code-barres</span>
            </Button>
          ) : null}
        </div>
      </PopoverTrigger>
      
      <PopoverContent 
        className="w-[var(--radix-popover-trigger-width)] p-0" 
        align="start"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        {/* Historique récent */}
        {showHistory && (
          <div className="p-2">
            <div className="flex items-center justify-between text-xs text-muted-foreground mb-2 px-2">
              <span className="flex items-center gap-1.5">
                <Clock className="h-3 w-3" />
                Recherches récentes
              </span>
              <button 
                onClick={clearHistory}
                className="hover:text-foreground transition-colors"
              >
                Effacer
              </button>
            </div>
            <div className="space-y-0.5">
              {history.slice(0, 5).map((term, index) => (
                <div
                  key={term}
                  className={cn(
                    "flex items-center justify-between px-2 py-1.5 rounded-md cursor-pointer group",
                    index === selectedIndex ? "bg-muted" : "hover:bg-muted"
                  )}
                  onClick={() => handleSelect(term)}
                >
                  <span className="text-sm truncate">{term}</span>
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label={`Supprimer "${term}" de l'historique`}
                    className="h-5 w-5 opacity-0 group-hover:opacity-100 transition-opacity"
                    onClick={(e) => {
                      e.stopPropagation();
                      removeFromHistory(term);
                    }}
                  >
                    <X className="h-3 w-3" />
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}
        
        {/* Suggestions basées sur la saisie */}
        {showSuggestions && (
          <div className="p-2">
            <div className="text-xs text-muted-foreground mb-2 px-2 flex items-center gap-1.5">
              <Search className="h-3 w-3" />
              Suggestions
            </div>
            <div className="space-y-0.5">
              {suggestions.map((suggestion, index) => (
                <div
                  key={`${suggestion.text}-${suggestion.type}`}
                  className={cn(
                    "flex items-center gap-2 px-2 py-1.5 rounded-md cursor-pointer",
                    index === selectedIndex ? "bg-muted" : "hover:bg-muted"
                  )}
                  onClick={() => handleSelect(suggestion.text)}
                >
                  {suggestion.type === 'barcode' ? (
                    <Barcode className="h-3.5 w-3.5 text-emerald-600 dark:text-emerald-400 flex-shrink-0" />
                  ) : suggestion.type === 'product' ? (
                    <Package className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
                  ) : (
                    <Tag className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
                  )}
                  <div className="flex flex-col min-w-0 flex-1">
                    <span className="text-sm truncate">
                      <HighlightedText text={suggestion.text} highlight={value} />
                    </span>
                    {suggestion.type === 'barcode' && suggestion.gtin && (
                      <span className="text-xs text-muted-foreground font-mono">
                        {suggestion.gtin}
                      </span>
                    )}
                  </div>
                  <span className={cn(
                    "text-[10px] px-1.5 py-0.5 rounded-full flex-shrink-0",
                    suggestion.type === 'barcode'
                      ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-300"
                      : suggestion.type === 'product' 
                        ? "bg-primary/10 text-primary" 
                        : "bg-secondary text-secondary-foreground"
                  )}>
                    {suggestion.type === 'barcode' ? 'Code-barres' : suggestion.type === 'product' ? 'Produit' : 'Marque'}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
        
        {/* Message si code-barres non trouvé */}
        {value.length >= 4 && /^\d{4,}$/.test(value.trim()) && suggestions.length === 0 && (
          <div className="px-3 py-4 text-center border-t bg-muted/30">
            <div className="flex items-center justify-center gap-2 text-muted-foreground mb-1">
              <Barcode className="h-4 w-4" />
              <span className="text-sm font-medium">Code-barres non trouvé</span>
            </div>
            <p className="text-xs text-muted-foreground">
              Ce produit n'est pas dans notre base de rappels ou le code-barres n'est pas renseigné pour ce rappel.
            </p>
          </div>
        )}
      </PopoverContent>
    </Popover>

    {scannerOpen && (
      <Suspense fallback={null}>
        <BarcodeScannerDialog
          open={scannerOpen}
          onOpenChange={setScannerOpen}
          onScan={handleScan}
        />
      </Suspense>
    )}
    </>
  );
};
